<!-- eslint-disable no-plusplus -->
<!-- eslint-disable comma-dangle -->
<template>
  <div class="flex flex-col gap-2" :key="`registeredevent-${currentPokerRunEvent?.id}`">
    <EventHeader :poker-run-event="currentPokerRunEvent" :show-type="true"></EventHeader>
    <ExtraCardMessage
      v-if="!showWaiver"
      >
    </ExtraCardMessage>
    <TrackerRequiredMessage
      v-if="!showWaiver && requiresCheckin && userQRCodeRef"
      :user-q-r-code-ref="userQRCodeRef"
      :tracker-required-for-this-stop="trackerRequiredForThisStop"
      :next-stop="nextStop"
    >
    </TrackerRequiredMessage>
    <UserQRCode ref="userQRCodeRef" @close="handleUserQRClose()" :enable-modal="true" :modal-message="userQRCodeMessage"
      :modal-warning="userQRCodeWarning" />
    <LocationRequiredMessage v-if="locationError"></LocationRequiredMessage>
    <CartItemNotification v-if="(currentEventParticipants?.length ?? 0) > 0"></CartItemNotification>
    <MissedStopMessage v-if="firstMissedStop && showMissedStopMessage" :name="firstMissedStop.name"
      @close="clearMissedStopMessage"></MissedStopMessage>
    <AcceptWaiver v-if="showWaiver"></AcceptWaiver>
    <div class="flex flex-col gap-2" v-else>
      <AppButton title="My QR Code" icon-left="fa-qrcode" @click="showQRCode" />
      <AppButton title="Scan QR code for next card" @click="stopCheckIn" v-if="currentPokerRunEvent?.eventSettings?.cardReleaseType === Constants.CARD_RELEASE_TYPE_MANUAL || currentPokerRunEvent?.eventSettings?.cardReleaseType === Constants.CARD_RELEASE_TYPE_GEO" />
      <AppRouterLink buttonType="Secondary" variant="link"
       route-name="Leaderboard" :route-params="{ id: currentPokerRunEvent?.id }" title="View Leaderboard" />

      <AppSectionHeader>Your Hands</AppSectionHeader>
      <template v-for="(currentEventParticipant, index) in participantWithLeaderboard" :key="`participant-${index}-${
            currentEventParticipant.hand?.cardSlots.filter((x) => x.show).length
          }`">
        <Hand v-if="currentEventParticipant?.hand" :extra-card="currentEventParticipant.extraCard"
          :hand="currentEventParticipant.hand" :name="currentEventParticipant.name ?? `Hand ${index + 1}`"
          :is-offline="isOffline" @rename="(newName) => renameParticipant(currentEventParticipant, newName)"></Hand>
        <div>
        </div>
      </template>
      <AppRouterLink v-if="currentEventParticipants?.length > 0" variant="link" :disabled="isOffline"
        route-name="ManageHands" :route-params="{ id: currentPokerRunEvent?.id }" title="Manages hands"
         />
      <AppSectionHeader v-if="hasOtherItems">
        {{ participantWithLeaderboard.length > 0 ? "Other items" : "Purchased event items" }}</AppSectionHeader>
      <template v-for="item in registrationLineItems.filter(x=> x.itemType === Constants.CUSTOM_TYPE)" :key="`item-${item.itemId}`">
        <PurchasedLineItem :description="item.name" :quantity="item.quantity" />
      </template>
      <template v-for="item in registrationLineItems.filter(x=> x.itemType === Constants.DONATION_TYPE)" :key="`item-${item.itemId}`">
        <DonationLineItem :description="item.name" :amount="item.amount" />
      </template>
      <AppRouterLink :disabled="isOffline || currentPokerRunEvent?.eventSettings?.registrationStatus === 'closed'"  buttonType="Secondary"  route-name="Register"
        :route-params="{ id: currentPokerRunEvent?.id }" title="Buy hands, cards and other items"
        />

      <AppSectionHeader>Event stops</AppSectionHeader>
      <div>
        <AppExpandCollapse v-model="areAllStopsExpanded" @update:model-value="toggleAllStops" />
        <template v-for="(item, index) in orderedStops" :key="`stop-${index}-${firstParticipantRecord?.id}`">
          <StopView class="spacy-y-2" :showOptions="false"
            :showDate="currentPokerRunEvent.eventSettings.isMultidayEvent" :stop="item"
            :expanded="expandedStops[item.order]" :is-current-stop="item.order === nextStop?.order"
            :show-checked-in="isEventActive(currentPokerRunEvent)"
            :checked-in-status="stopCheckedInStatuses[item.order]"
            :show-map="true"
            :cardReleaseType="currentPokerRunEvent.eventSettings.cardReleaseType"
            @update:expanded="(expanded: boolean) => handleStopExpansion(item.order, expanded)" />
        </template>
      </div>

      <div v-if="canCheckin && hasNextStop && !requiresCheckin" class="py-4 w-full bg-white text-center fixed bottom-0 md:static">
        <p class="p-4 text-center text-lg font-semibold w-full bg-primary"
          v-if="currentPokerRunEvent?.eventSettings?.cardReleaseType === Constants.CARD_RELEASE_TYPE_SCHEDULE || currentPokerRunEvent?.eventSettings?.cardReleaseType === Constants.CARD_RELEASE_TYPE_GEO">
          <span class="text-white" v-if="nextStop">
            {{ nextStopMessage }}
          </span>
        </p>
      </div>
      <div v-if="newCheckin && newCheckinStop && currentPokerRunEvent?.eventSettings?.cardReleaseType !== 'manual'"
        class="fixed bottom-0 left-0 right-0 bg-white shadow-lg text-center px-6 py-4 z-50" role="alert">
        <div class="mx-2 pb-2">
          <span>
            You have a new card for stop{{ newCheckinStop ? ` ${newCheckinStop}` : "" }}.
          </span>
        </div>
        <AppButton title="Close"  @click="clearNewCheckin" />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, watch, inject } from "vue";
import { RouteLocationNamedRaw, RouteLocationRaw, useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import EventHeader from "@/components/EventHeader.vue";
import ShowCloseOptions from "@/components/ShowCloseOptions.vue";
import EventStop from "@/components/EventStop.vue";
import Hand from "@/components/Hand.vue";
import UserQRCode from "@/components/UserQRCode.vue";
import CartItemNotification from "@/components/CartItemNotification.vue";
import ExtraCardMessage from "@/components/ExtraCardMessage.vue";
import TrackerRequiredMessage from "@/components/TrackerRequiredMessage.vue";
import PurchasedLineItem from "@/components/PurchasedLineItem.vue";
import DonationLineItem from "@/components/DonationLineItem.vue";
import LocationRequiredMessage from "@/components/LocationRequiredMessage.vue";
import StopView from "@/components/StopView.vue";
import { useUserProfileStore } from "@/store/UserProfile";
import { useEventParticipantStore } from "@/store/EventParticipant";
import useLeaderboard from "@/modules/useLeaderboard";
import UserTransactionDataService from "@/services/UserTransactionDataService";
import EventTrackerDataService from "@/services/EventTrackerDataService";
import useDate from "@/modules/useDate";
import useGeoFencing from "@/modules/useGeoFencing";
import Constants from "@/models/Constants";
import { Stop } from "@models/Stop";
import AcceptWaiver from "./AcceptWaiver.vue";
import MissedStopMessage from "./MissedStopMessage.vue";
import AppButton from '@/components/AppButton.vue';
import AppRouterLink from "./AppRouterLink.vue";
import useStops from "@/modules/useStops";
import { EventParticipant } from "@models/EventParticipant";
import useEvent from "@/modules/useEvent";

const { watchLocations, cleanupWatch } = useGeoFencing();
const { getDate, formatDatetime, formatDate, formatTime, formatISODate } = useDate();
const props = defineProps({
  eventId: {
    type: String,
    required: true,
  },
});

const today = new Date();
today.setHours(0, 0, 0, 0);
const { leaderboard, getLeaderboard, unsubscribeLeaderboard } = useLeaderboard();
const router = useRouter();
const userProfileStore = useUserProfileStore();
const {
  currentEventParticipants,
  currentPokerRunEvent,
  ownerRegistrations,
  assignedRegistrations,
  registrationLineItems,
  canPurchaseExtraCards,
  canPurchaseAddons,
  newCheckin,
  newCheckinStop,
  isOffline,
  userWaiver,
  participantTrackers,
  firstMissedStop,
  showMissedStopMessage,
  hasOtherItems
} = storeToRefs(useEventParticipantStore());
const {
  clearNewCheckin,
  loadEventParticipant,
  isAlreadyCheckedIn,
  renameParticipant,
  scheduleCheckinOffine,
  checkIn,
  loadParticipantTrackers,
  clearMissedStopMessage,
} = useEventParticipantStore();


const { isEventActive } = useEvent();

const firstParticipantRecord = computed(() => {
  if (currentEventParticipants.value && currentEventParticipants.value.length > 0) {
    return currentEventParticipants.value[0];
  }
  return {id:"1"} as EventParticipant;
});

const { nextStop,
  hasNextStop, orderedStops, getStopCheckedInStatus: getStopCheckedInStatus, canPurchaseExtraCard
} = useStops(currentPokerRunEvent, firstParticipantRecord);

const expandedStops = ref<Record<number, boolean>>({});

const areAllStopsExpanded = computed(() => {
  return orderedStops.value.length > 0 &&
    orderedStops.value.every(stop => expandedStops.value[stop.order]);
});

const handleStopExpansion = (stopOrder: number, expanded: boolean) => {
  expandedStops.value = {
    ...expandedStops.value,
    [stopOrder]: expanded
  };
};

const toggleAllStops = () => {
  const newState = !areAllStopsExpanded.value;
  orderedStops.value.forEach(stop => {
    expandedStops.value[stop.order] = newState;
  });
};


const locationError = ref(false);
const userQRCodeRef = ref<{ toggleShowModal: () => void } | null>(null);

const handleUserQRClose = async () => {
  await loadParticipantTrackers();
};


const trackerRequiredForThisStop = computed(() => {
  const tracker = currentPokerRunEvent.value?.trackers?.find(
    (x) => x.requiredStopNumber === nextStop.value?.order
  );
  if (tracker) {
    const thisParticipantTrackers = participantTrackers.value.filter((x) =>
      x.checkIns.some((y) => y.trackerId === tracker.trackerId)
    );
    if (thisParticipantTrackers.length !== assignedRegistrations.value.length) {
      return tracker;
    }
  }
  return null;
});

const requiresCheckin = computed(() => {
  return trackerRequiredForThisStop.value != null;
});

const nextStopMessage = computed(() => {
  const eod = new Date();
  eod.setHours(23, 59, 59, 999);
  const isNextStopToday = new Date(nextStop.value.expectedArrivalTime as string) <= eod;
  const nextStopTimeText = isNextStopToday
    ? formatTime(nextStop.value.expectedArrivalTime as string)
    : formatDatetime(nextStop.value.expectedArrivalTime as string);
  return currentPokerRunEvent.value?.eventSettings?.cardReleaseType === Constants.CARD_RELEASE_TYPE_GEO ?
    `Next card at ${nextStop.value.name}` : `Next card at ${nextStopTimeText}`;

});

const lastEventStop = computed(() => {
  if (currentPokerRunEvent.value) {
    return currentPokerRunEvent.value.stops[currentPokerRunEvent.value.stops.length - 1];
  }
  return null;
});


const participantWithLeaderboard = computed(() =>
  currentEventParticipants.value?.map((x) => ({
    ...x,
    leaderboard: leaderboard.value?.find((y) => y.participantId === x.id),
  }))
);

const canCheckin = computed(
  () =>
    currentPokerRunEvent.value?.eventDate &&
    currentPokerRunEvent.value?.status === "active" &&
    currentPokerRunEvent.value.eventDate.substring(0, 10) <= formatISODate(today)
);

const showQRCode = () => {
  userQRCodeRef.value?.toggleShowModal();
};

const showWaiver = computed(() => {
  return !userWaiver.value?.acceptedOn && currentPokerRunEvent.value?.waiver;
});

const maxCapacity = computed(() => {
  const hasCap =
    currentPokerRunEvent.value &&
    currentPokerRunEvent.value?.eventSettings?.registrationCap &&
    currentPokerRunEvent.value?._internal?.registrationCount;

  return hasCap
    ? (currentPokerRunEvent.value?._internal?.registrationCount ?? 0) >=
    currentPokerRunEvent.value?.eventSettings?.registrationCap
    : false;
});

const canPurchaseExtraCardsAndValidForEvent = computed(
  () =>
    currentPokerRunEvent.value?.status === "active" &&
    currentPokerRunEvent.value?.eventSettings.registrationStartDate <=
    formatISODate(new Date()) &&
    currentPokerRunEvent.value?.eventDate.substring(0, 10) >= formatISODate(today) &&
    currentPokerRunEvent.value?.eventSettings?.registrationStatus !== "closed" &&
    !isOffline.value &&
    currentPokerRunEvent.value?.eventSettings?.allowInAppPurchase === true &&
    canPurchaseExtraCards.value
);

const canRegister = computed(
  () =>
    currentPokerRunEvent.value?.status === "active" &&
    currentPokerRunEvent.value?.eventSettings.registrationStartDate <=
    formatISODate(new Date()) &&
    currentPokerRunEvent.value?.eventDate.substring(0, 10) >= formatISODate(today) &&
    !maxCapacity.value &&
    currentPokerRunEvent.value?.eventSettings?.registrationStatus !== "closed" &&
    !isOffline.value &&
    currentPokerRunEvent.value?.eventSettings?.allowInAppPurchase === true
);

const userQRCodeMessage = computed(() =>
  currentEventParticipants.value.length > 1
    ? `You are scanning ${assignedRegistrations.value.length} hand${assignedRegistrations.value.length === 1 ? "" : "s"
    }.`
    : ""
);

const userQRCodeWarning = computed(() => {
  const sharedRegistrations = ownerRegistrations.value.filter(
    (x) => x.status === "shared"
  );
  return sharedRegistrations.length > 0
    ? `${sharedRegistrations.length} shared hand${sharedRegistrations.length === 1 ? "" : "s"
    } will not be checked in.`
    : "";
});


const stopCheckIn = () => {
  router.push({ name: "CheckIn", params: { id: props.eventId } });
};

const visibilitychangeHandler = () => {

  console.log("document.visibilityState", document.visibilityState);
  if (document.visibilityState === "visible") {
    if (isOffline.value) {
      console.log("offline sync");
      scheduleCheckinOffine();
    }
  }
};
onMounted(() =>
  document.addEventListener("visibilitychange", visibilitychangeHandler)
);

onBeforeUnmount(() => {
  document.removeEventListener("visibilitychange", visibilitychangeHandler);
  unsubscribeLeaderboard();
  if (currentPokerRunEvent.value?.eventSettings?.cardReleaseType === Constants.CARD_RELEASE_TYPE_GEO) {
    cleanupWatch();
  }
}
);


const formatCurrency = (amount: number | null | undefined) => {
  if (amount === null || amount === undefined) return '$0.00';
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

const register = () => {
  UserTransactionDataService.warmup();
  const fundraiser = assignedRegistrations.value.find((x) => x.fundraiser)?.fundraiser ??
    ownerRegistrations.value.find((x) => x.fundraiser)?.fundraiser;

  if (fundraiser) {
    router.push({
      name: "Register",
      params: { id: currentPokerRunEvent.value.id },
      query: { fundraiser: fundraiser }
    });
  } else {
    router.push({
      name: "Register",
      params: { id: currentPokerRunEvent.value.id }
    });
  }
};


const initalize = async () => {
  if (isOffline.value) {
    return;
  }
  await loadEventParticipant(props.eventId as string, userProfileStore.profile.id);

  if (currentPokerRunEvent.value.eventSettings.gameFormat === "Trade Show") {
    router.replace({ name: "RegisteredEventLite", params: { id: props.eventId as string } });
    return;
  }

  const participantIds = currentEventParticipants.value?.map((x) => x.id as string);
  getLeaderboard(props.eventId as string, participantIds);

  if (currentPokerRunEvent.value.eventSettings?.cardReleaseType === Constants.CARD_RELEASE_TYPE_GEO) {
    const permissionStatus = await navigator.permissions.query({ name: "geolocation" });
    if (permissionStatus.state === "denied") {
      locationError.value = true;
    }
    watchLocations(currentPokerRunEvent.value.stops, currentEventParticipants, (stop: Stop, checkinMetadata: any) => {
      if (canCheckin.value) {
        checkIn(stop.checkInCode, checkinMetadata);
      }
    });
  }

  await loadParticipantTrackers();

};
initalize();
const stopCheckedInStatuses = computed(() => {
  return Object.fromEntries(
    orderedStops.value.map(item => [
      item.order,
      getStopCheckedInStatus(item.order).value
    ])
  )
})
</script>
